<template>
  <content-dialog
    v-bind="$attrs"
    v-model="dialogOpen"
    fullscreen-tablet
    persistent
    height="70vh"
    width="650px"
    :loading="loading"
    :card-visible="!!item"
    :title="(mode === 'add' ? 'Create' : 'Edit') + ' Inventory'"
    :tags="[helpCodes.inventory]"
    @hide="$emit('close')"
  >
    <template v-if="mode === 'add'" #toolbar-buttons>
      <q-btn
        size="12px"
        rounded
        outline
        no-caps
        class="q-mr-sm"
        icon-right="mdi-book-search"
        :dense="windowWidth < 600"
        :label="windowWidth < 600 ? 'Prefills' : 'Search & Add Prefills'"
        @click="close(), $emit('openPrefills')"
      />
    </template>

    <template v-if="!!item">
      <q-list dense>
        <sticky-header>{{ baseVisibleItemName(item) }} Overview</sticky-header>

        <detail-item
          icon="mdi-format-list-checks"
          name="Type"
          with-input
          required
          :name-spacing="nameSpacing"
        >
          <q-btn-toggle
            v-model="item.type"
            :options="typeOptions"
            no-caps
            @update:model-value="onInputType"
          />
        </detail-item>

        <detail-item
          icon="mdi-book-open-page-variant-outline"
          name="Code"
          with-input
          required
          :name-spacing="nameSpacing"
        >
          <q-input
            v-model="item.code"
            dense
            outlined
            clearable
            placeholder="ABC123"
            counter
            :maxlength="30"
            :error="!!errors['code']"
            :error-message="errors['code'] ? errors['code'][0] : null"
            hide-bottom-space
            hint="Unique identifier code for this item"
          />
        </detail-item>

        <detail-item
          icon="mdi-text-short"
          name="Name"
          with-input
          required
          :name-spacing="nameSpacing"
        >
          <q-input
            v-model="item.name"
            dense
            outlined
            clearable
            :maxlength="nameLengthLimit"
            :error="!!errors['name'] || (item.name || '').length > nameLengthLimit"
            :error-message="errors['name'] ? errors['name'][0] : null"
            hide-bottom-space
            counter
          />
        </detail-item>

        <detail-item
          v-if="item.type === 'PRODUCT' && (company.industry_settings.setup_prefill_inventory || !!item.system_type_id)"
          name="Category"
          with-input
          :name-spacing="nameSpacing"
        >
          <q-select
            v-model="item.system_type_id"
            :options="options ? options.types : []"
            :loading="loadingOptions"
            :error="!!errors['system_type_id']"
            :error-message="errors['system_type_id'] ? errors['system_type_id'][0] : null"
            option-value="id"
            option-label="name"
            emit-value
            map-options
            outlined
            dense
            clearable
            @update:model-value="onInputSystemType"
          >
            <template #option="scope">
              <q-select-color-item
                v-bind="scope.itemProps"
                :label="scope.opt.name"
                :color="scope.opt.color"
                hex
              />
            </template>
          </q-select>
        </detail-item>
      </q-list>

      <q-list
        v-if="item.is_selling"
        dense
        class="q-mt-md"
      >
        <sticky-header>
          Invoice / Quotation Details
        </sticky-header>

        <page-banner
          dense
          color="blue-grey-1"
          class="q-mx-sm q-mb-sm"
          caption="Unit Price and GST options may be applicable if this item is used on documents such as invoices or quotations"
        />

        <detail-item
          icon="mdi-currency-usd"
          with-input
          name="Selling Unit Price"
          :name-spacing="nameSpacing"
        >
          <q-input
            :model-value="item.selling_unit_price"
            type="number"
            min="0"
            max="99999"
            dense
            outlined
            prefix="$"
            step="0.01"
            hide-bottom-space
            :error="!!errors['selling_unit_price']"
            :error-message="errors['selling_unit_price'] ? errors['selling_unit_price'][0] : null"
            @update:model-value="onUnitPriceInput"
          />
        </detail-item>

        <detail-item
          icon="mdi-percent"
          with-input
          name="Markup Percentage"
          :name-spacing="nameSpacing"
        >
          <q-input
            v-model="item.markup_percentage"
            type="number"
            dense
            outlined
            suffix="%"
            step="1"
            class="full-width-mobile"
            style="width: 125px;"
            :min="0"
            :max="100"
            :error="!!errors['markup_percentage']"
            :error-message="errors['markup_percentage'] ? errors['markup_percentage'][0] : null"
            hide-bottom-space
          />
        </detail-item>

        <detail-item
          with-input
          required
          name="GST"
          :name-spacing="nameSpacing"
        >
          <q-select
            v-model="item.selling_tax_type"
            :options="taxStatusOptions"
            map-options
            emit-value
            outlined
            dense
            hide-bottom-space
            :error="!!errors['selling_tax_type']"
            :error-message="errors['selling_tax_type'] ? errors['selling_tax_type'][0] : null"
          />
        </detail-item>

        <detail-item
          name="Description"
          with-input
          :name-spacing="nameSpacing"
        >
          <q-input
            v-model="item.description"
            type="textarea"
            dense
            outlined
            clearable
            :error="!!errors['description']"
            :error-message="errors['description'] ? errors['description'][0] : null"
            hide-bottom-space
            hint="The description supplied here can be used on new line items"
          />
        </detail-item>

        <detail-item
          icon="mdi-file-document-edit-outline"
          with-input
          name="Document Prefill"
          :name-spacing="nameSpacing"
        >
          <div>
            <q-select
              v-model="item.prefill_column"
              :options="inventoryPrefillOptions"
              map-options
              emit-value
              outlined
              dense
              hint="Value to prefill from this item onto new line items"
              :error="!!errors['prefill_column']"
              :error-message="errors['prefill_column'] ? errors['prefill_column'][0] : null"
            />
          </div>
        </detail-item>
      </q-list>

      <q-list dense class="q-mt-md">
        <sticky-header>
          {{ baseVisibleItemName(item, selectedSystemType) }} Details
        </sticky-header>

        <metadata-edit-list
          v-model="item.metadata"
          :options="options ? options.defaults : []"
          :name-spacing="nameSpacing"
          without-color
        >
          <template #no-results>
            <q-item-wrapper label="No additional details" />
          </template>
        </metadata-edit-list>
      </q-list>
    </template>

    <template #bottom>
      <q-toolbar v-if="!!item">
        <q-space />
        <q-btn
          label="Save"
          icon-right="done"
          color="green"
          @click="$emit('save', item)"
        />
      </q-toolbar>
    </template>
  </content-dialog>
</template>

<script>
import inventoryMixin from './mixin'

import { generatePrepopulatedMds } from 'components/partials/metadata/metadataMixin'
import { dineroFromFloat, floatFromDinero } from 'components/app/accounting/documents/calculationMixin'

export default {
  mixins: [inventoryMixin],
  props: {
    errors: {
      type: Object,
      default: () => {
        return {}
      }
    },
    mode: {
      type: String,
      default: 'add'
    }
  },
  emits: ['close', 'reset', 'save', 'openPrefills'],
  data () {
    return {
      loadingOptions: false,
      options: null,
      nameSpacing: 175,
      loading: false,
      item: null,
      dialogOpen: false,
      taxStatusOptions: [
        { value: 'INCLUSIVE', label: 'Unit price includes GST (Inclusive)' },
        { value: 'EXCLUSIVE', label: 'Unit price excludes GST (Exclusive)' },
        { value: 'NONE', label: 'Item cannot contain GST' }
      ],
      typeOptions: [
        { value: 'PRODUCT', label: 'Product' },
        { value: 'SERVICE', label: 'Service' }
      ],
      inventoryPrefillOptions: [
        { value: 'NAME', label: 'Item Name' },
        { value: 'DESCRIPTION', label: 'Item Description' }
      ]
    }
  },
  computed: {
    nameLengthLimit () {
      return this.options && this.options.inventory_name_length ? this.options.inventory_name_length : 50
    },
    selectedSystemType () {
      if (!this.options || !this.item.system_type_id) {
        return null
      }
      return this.options.types.find(item => item.id === this.item.system_type_id)
    }
  },
  watch: {
    'item.type' (val) {
      if (val === 'SERVICE') {
        this.item.is_tracked = false
      }
    }
  },
  methods: {
    open (item) {
      if (!this.dialogOpen) {
        this.loadDefaults()
      }
      this.dialogOpen = true
      this.item = JSON.parse(JSON.stringify(item))
    },
    close () {
      this.dialogOpen = false
      this.item = null
    },
    onInputSystemType (val) {
      if (!val || (this.mode !== 'add' && this.item.metadata.length > 0)) {
        return
      }

      const systemType = this.options.types.find(item => item.id === val)
      if (!systemType) {
        return
      }

      const filteredDefaults = this.options.defaults.filter(item => item.system_type_id === systemType.id || item.system_type_id === systemType.parent_id)
      this.item.metadata = generatePrepopulatedMds(filteredDefaults)
    },
    onInputType (val) {
      if (val === 'SERVICE') {
        this.item.system_type_id = null
      }
    },
    onUnitPriceInput (val) {
      if (isNaN(val) || !val || val < 0 || val > 99999) {
        this.item.selling_unit_price = 0
      }
      else {
        // Truncate to 2 decimal places
        this.item.selling_unit_price = floatFromDinero(dineroFromFloat(val))
      }
    },
    async loadDefaults () {
      this.loadingOptions = true
      const { status, payload } = await this.$get('v1/inventory/defaults')
      this.loadingOptions = false
      if (status === 'success') {
        this.options = payload
      }
    }
  }
}
</script>
